import { setLinkAriaCurrent } from '@/core/utils/a11y'
import type { TaxiNavigateInEvent } from '@/types/taxi'
import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

import BaseSection from '@/sections/base'
import HeaderCartControl from '@/components/header/headerCartControl'

const selectors = {
  layer: '[data-header-layer]',
  link: '.header-nav__link',
  label: '.header-nav__label'
}

const classes = {
  blendLayer: 'header-layer--blend',
  hoverLayer: 'header-layer--hover'
}

const attrs = {
  hover: 'data-hover'
}

// Stripped from the clone so it is neither interactive nor picked up as a second
// live component / duplicate id. The toggle attributes stay: the CSS hides/shows
// those buttons by them, and the clone must keep layer 1's exact layout. The layer
// is inert with pointer-events: none, so they never act as real toggles.
const CLONE_STRIP_ATTRS = [
  'id',
  'href',
  'aria-label',
  'aria-controls',
  'aria-expanded',
  'aria-current',
  'data-component',
  'data-count',
  'data-header-layer'
]

export default class HeaderSection extends BaseSection {
  static TYPE = 'header'

  // Null while the bag is hidden via the section's "Show cart" setting (soft launch)
  headerCartControl: HeaderCartControl | null

  // Unblended clone of the header that draws the hovered label in true red.
  // The blended layer can't show a fixed colour (difference inverts it against the
  // page), so the hover state is mirrored onto a second fixed layer above it.
  hoverLayer: HTMLElement
  links: HTMLElement[]
  hoverLinks: HTMLElement[]

  constructor(container: HTMLElement) {
    super(container)

    const cartControlEl = this.qs(HeaderCartControl.SELECTOR)
    this.headerCartControl = cartControlEl ? new HeaderCartControl(cartControlEl) : null

    const layer = this.qs(selectors.layer) as HTMLElement
    this.hoverLayer = this.buildHoverLayer(layer)
    this.container.appendChild(this.hoverLayer)

    this.links = Array.from(layer.querySelectorAll<HTMLElement>(selectors.link))
    this.hoverLinks = Array.from(this.hoverLayer.querySelectorAll<HTMLElement>(selectors.link))

    this.links.forEach((link, i) => {
      // Touch has no hover state (see _header.css) — a tap must not flash the red label
      link.addEventListener('pointerenter', e => {
        if (e.pointerType !== 'touch') this.setHover(i, true)
      })
      link.addEventListener('pointerleave', () => this.setHover(i, false))
    })
  }

  buildHoverLayer(layer: HTMLElement) {
    const clone = layer.cloneNode(true) as HTMLElement

    clone.classList.replace(classes.blendLayer, classes.hoverLayer)
    clone.setAttribute('aria-hidden', 'true')
    clone.setAttribute('inert', '')

    clone.querySelectorAll('*').forEach(el => {
      CLONE_STRIP_ATTRS.forEach(attr => el.removeAttribute(attr))
    })

    return clone
  }

  setHover(index: number, on: boolean) {
    const source = this.links[index]
    const mirror = this.hoverLinks[index]
    if (!source || !mirror) return

    if (on) {
      // Copy the live text (the bag count changes at runtime)
      const sourceLabel = source.querySelector(selectors.label)
      const mirrorLabel = mirror.querySelector(selectors.label)
      if (sourceLabel && mirrorLabel) mirrorLabel.textContent = sourceLabel.textContent
    }

    mirror.toggleAttribute(attrs.hover, on)
  }

  onNavigateIn(e: TaxiNavigateInEvent) {
    const currentPath = new URL(e.detail.to.finalUrl).pathname
    const links = this.container.querySelectorAll<HTMLAnchorElement>('nav a[href]')

    links.forEach(link => {
      if (link.getAttribute('href')?.startsWith('#')) return
      setLinkAriaCurrent(link, currentPath)
    })
  }

  onUnload(e?: ThemeEditorSectionUnloadEvent) {
    this.hoverLayer.remove()
    super.onUnload(e)
  }
}
